import './styles.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import DomainRedirect from './components/Layout/DomainRedirect';
import { config } from './config/env';
import { queryClient } from './lib/queryClient';
import { DeviceProvider } from './providers/DeviceProvider';
import { GeolocationProvider } from './providers/GeolocationProvider';
import { MarkerIoProvider } from './providers/MarkerIoProvider';
import { routeTree } from './routeTree.gen';
import { initFeatureFlags } from './stores/featureFlagsStore';
import { createLogger } from './utils/logger';

// Extend Window interface for Sentry loader
declare global {
  interface Window {
    Sentry?: {
      onLoad: (callback: () => void) => void;
      captureException: (error: Error) => void;
      captureMessage: (message: string, options?: { level?: string; extra?: Record<string, unknown> }) => void;
      getClient: () => {
        getOptions: () => {
          enabled: boolean;
          environment: string;
        };
      } | null;
    };
  }
}

const logger = createLogger('main');

// Configure Sentry environment settings after the loader has initialized
if (typeof window !== 'undefined' && window.Sentry) {
  window.Sentry.onLoad(() => {
    const client = window.Sentry?.getClient();
    if (client) {
      const options = client.getOptions();
      options.enabled = config.env !== 'development';
      options.environment = config.env;
    }
  });
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

try {
  initFeatureFlags();
} catch (error) {
  logger.error('Failed to initialize feature flags', { error });
}

// Render the app
const rootElement = document.getElementById('app');
if (!rootElement) {
  throw new Error('Failed to find root element');
}

if (!rootElement.innerHTML) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <DomainRedirect>
        <FeedbackWrapper>
          <GeolocationProvider>
            <QueryClientProvider client={queryClient}>
              <DeviceProvider>
                <RouterProvider router={router} />
              </DeviceProvider>
            </QueryClientProvider>
          </GeolocationProvider>
        </FeedbackWrapper>
      </DomainRedirect>
    </StrictMode>,
  );
}

function FeedbackWrapper({ children }: { children: React.ReactNode }) {
  if (!config.markerIoProjectId) {
    return children;
  }

  return <MarkerIoProvider>{children}</MarkerIoProvider>;
}
